import { Card } from "../ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import {
  BookOpen, Users, Truck, Calculator, Wallet, Landmark, BadgeCheck, MessageSquare,
  BarChart3, ShieldCheck, Settings, Wrench, Sparkles, FileText,
} from "lucide-react";

/** Manual del Funcionario: guía operativa por módulo del backoffice SISAUR. */
const Step = ({ n, children }) => (
  <li className="flex gap-3 py-1.5">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="flex-1 text-slate-700 leading-relaxed text-sm">{children}</span>
  </li>
);
const Tip = ({ children }) => (
  <div className="mt-2 p-2.5 bg-green-50 border border-green-100 rounded-md flex gap-2 text-xs text-green-900">
    <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
    <div>{children}</div>
  </div>
);

export default function ManualFuncionario() {
  return (
    <div className="space-y-6" data-testid="manual-funcionario">
      <Card className="p-6 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/40">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-orange-600 text-white">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold text-orange-900">Manual del Funcionario</h2>
            <p className="text-orange-900/80 mt-1">
              Guía operativa de todos los módulos del backoffice SISAUR. Cada sección describe el flujo típico y los permisos requeridos.
            </p>
            <p className="text-xs text-orange-800/70 mt-2">
              Los permisos granulares por módulo se gestionan desde <b>«Roles y Permisos»</b> (solo Superadmin).
            </p>
          </div>
        </div>
      </Card>

      <Accordion type="multiple" defaultValue={["f-1"]} className="space-y-3">
        {/* Dashboard */}
        <AccordionItem value="f-1" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><BarChart3 className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Dashboard ejecutivo</p><p className="text-xs text-slate-500">KPIs, recaudado del día/mes, deuda global, reclamos</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <p className="text-sm text-slate-600 mb-2">Vista de arranque para todos los roles. Muestra:</p>
            <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1">
              <li>Recaudado <b>hoy</b> y <b>este mes</b> en Bs.</li>
              <li>Deuda total pendiente y N° de pagos por validar.</li>
              <li>Reclamos abiertos por estado.</li>
              <li>Gráfica de recaudación de los últimos 7 días.</li>
            </ul>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">dashboard.view</code></Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Definiciones */}
        <AccordionItem value="f-2" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Settings className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Definiciones</p><p className="text-xs text-slate-500">Bancos, cuentas, códigos presupuestarios, sectores, cotización</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Configura los <b>bancos</b> autorizados y sus <b>cuentas bancarias</b> receptoras.</Step>
              <Step n={2}>Define <b>códigos presupuestarios</b> para asociarlos a las liquidaciones.</Step>
              <Step n={3}>Mantén la <b>Cotización de Moneda</b> (TCMMV) activa — se usa para calcular liquidaciones automáticamente.</Step>
              <Step n={4}>Administra <b>sectores</b> y municipios que se muestran en el alta de contribuyentes.</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">definiciones.view</code>, <code className="bg-slate-100 px-1 rounded">definiciones.editar</code></Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Aseo Urbano */}
        <AccordionItem value="f-3" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Truck className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Aseo Urbano (Ordenanza / Tarifas)</p><p className="text-xs text-slate-500">154 tarifas Industrial (6 tramos) + No-Industrial (3 tramos)</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Dos tabs: <b>«No Industrial»</b> (comercios, servicios, oficinas) y <b>«Industrial»</b> (fábricas, plantas).</Step>
              <Step n={2}>Cada tarifa tiene código de clasificación, actividad específica y tarifas por tramo de m² del establecimiento.</Step>
              <Step n={3}>Puedes buscar por código, actividad o grupo. Los badges muestran cuántas tarifas hay por categoría.</Step>
              <Step n={4}>Solo Superadmin/Admin Financiero puede editar/crear tarifas.</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">aseo_urbano.view/editar</code>. Las tarifas se aplican automáticamente al generar liquidaciones.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Contribuyentes */}
        <AccordionItem value="f-4" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Users className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Contribuyentes</p><p className="text-xs text-slate-500">CRUD, importación masiva, inmuebles asociados</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Dos categorías: <b>«Con LAE»</b> (comerciantes con Licencia de Actividad Económica) y <b>«Sin LAE»</b> (residenciales).</Step>
              <Step n={2}>El <b>header</b> muestra contadores KPI: Total, Con LAE, Sin LAE, Suspendidos.</Step>
              <Step n={3}>Filtros por RIF, razón social, denominación, domicilio y estado. Paginación server-side (25/50/100/200 filas).</Step>
              <Step n={4}>Click en el header de una columna (RIF, Razón Social, Denominación) para ordenar ascendente/descendente.</Step>
              <Step n={5}>Botones por fila: <b>«Editar»</b>, <b>«Suspender/Reactivar»</b>, <b>«Eliminar»</b>. Al eliminar se borran los inmuebles asociados.</Step>
              <Step n={6}>Exporta a Excel/PDF/JSON con los filtros aplicados (hasta 5.000 filas).</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">contribuyentes.view/crear/editar/eliminar/suspender</code></Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Liquidaciones */}
        <AccordionItem value="f-5" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Calculator className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Liquidaciones (Planillas)</p><p className="text-xs text-slate-500">Generación mensual, PDF con QR, envío WhatsApp</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Busca el contribuyente (autocomplete server-side).</Step>
              <Step n={2}>Selecciona uno o varios <b>períodos pendientes</b>. Las líneas se generan automáticamente: <code className="text-xs bg-slate-100 px-1 rounded">TCMMV × área × tarifa</code>.</Step>
              <Step n={3}>Sólo roles con <code className="text-xs bg-slate-100 px-1 rounded">liquidaciones.editar_ajustes</code> (Superadmin, Director Hacienda) pueden modificar Descuento/Reintegro/Retención/Multa.</Step>
              <Step n={4}>Registra la declaración. Se emite un N° de planilla único y un PDF con logo, tabla de conceptos, total en Bs. + equivalencia USD/EUR y QR de pago.</Step>
              <Step n={5}>Botones por planilla: <b>Descargar PDF</b>, <b>Enviar por WhatsApp</b> (link wa.me pre-cargado).</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">liquidaciones.view/crear/anular</code> + opcionalmente <code>editar_ajustes</code>.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Recaudaciones */}
        <AccordionItem value="f-6" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Wallet className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Recaudaciones (Validar pagos)</p><p className="text-xs text-slate-500">Registrar pagos manuales, validar/rechazar, recibo PDF</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Dos tabs: <b>«Registrar pago»</b> (funcionario) y <b>«Historial»</b>.</Step>
              <Step n={2}>Para registrar: elige contribuyente + planillas a cubrir + banco + fecha + referencia + monto.</Step>
              <Step n={3}>Los pagos reportados por contribuyentes aparecen en <b>Pendiente validación</b>. Podés <b>«Validar»</b> o <b>«Rechazar»</b>.</Step>
              <Step n={4}>Al validar, se genera un <b>Nº de comprobante</b> (REC-NNNNNN/AAAA), se marcan las planillas como Pagadas y se envía notificación (WhatsApp semi-auto + Email si Resend está configurado).</Step>
              <Step n={5}>Botones por pago validado: <b>«Descargar recibo PDF»</b>, <b>«WhatsApp»</b>, <b>«Reenviar email»</b>, <b>«Anular»</b>.</Step>
            </ol>
            <Tip>El recibo trae QR — cualquier tercero puede verificar autenticidad escaneándolo.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Conciliación Bancaria */}
        <AccordionItem value="f-7" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Landmark className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Conciliación Bancaria</p><p className="text-xs text-slate-500">Auto-validación de pagos vía extracto (PDF/Excel/XLSX/HTML)</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Sube el extracto bancario en cualquier formato: <b>PDF</b>, <b>.xlsx</b> moderno, <b>.xls</b> BIFF clásico, <b>HTML</b> (Mercantil/Banesco), CSV.</Step>
              <Step n={2}>El sistema detecta el banco (BFC, Banesco, BDV, Mercantil…) y parsea todas las líneas de crédito.</Step>
              <Step n={3}>Regla de match: <b>últimos 6 dígitos de referencia</b> + <b>monto ±0.01 Bs</b> + <b>fecha ±1 día</b>.</Step>
              <Step n={4}>Los pagos matcheados se <b>auto-validan</b>: se asigna N° de comprobante, planillas Pagadas y se dispara notificación.</Step>
              <Step n={5}>Los movimientos sin match quedan visibles con estado no_match (para revisión manual).</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">conciliacion.view/upload</code></Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Solvencias */}
        <AccordionItem value="f-8" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><BadgeCheck className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Solvencias</p><p className="text-xs text-slate-500">Emitir, consultar, anular certificados oficiales</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Dos tabs: <b>«Emitir»</b> (buscar contribuyente + verificar elegibilidad + generar) e <b>«Historial»</b>.</Step>
              <Step n={2}>Un contribuyente es elegible si su último pago validado cubre el mes en curso.</Step>
              <Step n={3}>La emisión es <b>idempotente</b>: si ya hay solvencia vigente del mes, retorna la misma.</Step>
              <Step n={4}>Historial: descargar PDF, enviar por WhatsApp, anular (con motivo).</Step>
            </ol>
            <Tip>El PDF incluye QR que apunta a la verificación pública (sin login).</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Reportes */}
        <AccordionItem value="f-9" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><BarChart3 className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Reportes gerenciales</p><p className="text-xs text-slate-500">Recaudación por actividad · Ranking morosidad · Mini-CRM</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>KPIs globales: total recaudado / esperado / morosidad % / ticket promedio.</Step>
              <Step n={2}>Gráfica Top 10 actividades por recaudación (Recharts).</Step>
              <Step n={3}>Tabla completa con filtros por columna. Exporta a Excel/PDF/JSON.</Step>
              <Step n={4}><b>Ranking Top 20 morosidad</b>: contribuyentes con mayor deuda. Cada fila trae botón WhatsApp con mensaje pre-cargado. Al pulsarlo se registra automáticamente el intento de contacto.</Step>
              <Step n={5}>Botón <b>«Exportar todos»</b>: descarga Excel con hasta 1.000 deudores incluyendo links de WhatsApp y último contacto (mini-CRM).</Step>
            </ol>
            <Tip>Requiere: <code className="bg-slate-100 px-1 rounded">reportes.view/exportar</code>. Solo Superadmin y Director Hacienda por defecto.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Reclamos */}
        <AccordionItem value="f-10" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><MessageSquare className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Reclamos</p><p className="text-xs text-slate-500">Atención con ciclo de vida (7 estados) y auditoría</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>KPI chips filtran la lista por estado: abierto / en_revision / en_proceso / resuelto / cerrado / rechazado / reabierto.</Step>
              <Step n={2}>Click en un reclamo abre el detalle con la <b>línea de tiempo completa</b> del historial (usuario, fecha, comentario).</Step>
              <Step n={3}>Cambia estado con motivo (obligatorio si rechazas). Solo se muestran las transiciones válidas desde el estado actual.</Step>
              <Step n={4}>Auto-cierre a los 7 días si queda en <b>resuelto</b> sin actividad.</Step>
            </ol>
          </AccordionContent>
        </AccordionItem>

        {/* Configuración + Acciones masivas */}
        <AccordionItem value="f-11" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Settings className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Configuración & Acciones masivas</p><p className="text-xs text-slate-500">Datos institucionales, parámetros, Liquidación General LAE</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Datos institucionales: alcaldía, alcalde, contacto, WhatsApp de atención.</Step>
              <Step n={2}>Parámetros: multa %, día tope de mora, moneda, cotización activa.</Step>
              <Step n={3}><b>«Acciones masivas → Liquidación General con LAE»</b>: previsualiza (dry-run) y ejecuta la generación de planillas para todos los contribuyentes con LAE al día de la fecha actual. Anula planillas previas superpuestas automáticamente.</Step>
            </ol>
            <Tip>Solo Superadmin/Admin Financiero. Cada ejecución queda en el log de acciones masivas.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Usuarios y Roles */}
        <AccordionItem value="f-12" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><ShieldCheck className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Usuarios Funcionarios & Roles/Permisos</p><p className="text-xs text-slate-500">RBAC granular por módulo y acción (solo Superadmin)</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}><b>«Usuarios Funcionarios»</b>: CRUD + reset de contraseña. Cada usuario tiene rol asignado.</Step>
              <Step n={2}><b>«Roles y Permisos»</b>: matriz de checkboxes por módulo × acción. Los cambios impactan a todos los usuarios de ese rol al instante.</Step>
              <Step n={3}>Roles predefinidos: <b>Superadmin</b>, <b>Admin Financiero (Director Hacienda)</b>, <b>Operador</b>, <b>Cajero</b>.</Step>
            </ol>
            <Tip>Los cambios se propagan sin necesidad de logout. Si a un usuario le quitan un permiso, la próxima navegación lo redirige a un módulo permitido.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Mantenimiento */}
        <AccordionItem value="f-13" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><Wrench className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Mantenimiento automático</p><p className="text-xs text-slate-500">Detección y reparación de datos huérfanos</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Scheduler interno cada 24 h detecta declaraciones marcadas como «Pagada» sin pago real que las respalde y las revierte a «Registrada».</Step>
              <Step n={2}>Podés lanzar la detección manual en dos modos: <b>«Solo detectar»</b> (dry-run) o <b>«Detectar y reparar»</b>.</Step>
              <Step n={3}>Cada ejecución queda en el historial con detalle de las declaraciones afectadas.</Step>
            </ol>
            <Tip>Requiere: rol Superadmin/Admin Financiero. Evita falsos positivos de solvencia por datos huérfanos.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* Notificaciones */}
        <AccordionItem value="f-14" className="border border-orange-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left"><FileText className="w-5 h-5 text-orange-700" />
              <div><p className="font-semibold text-orange-900">Notificaciones (WhatsApp & Email)</p><p className="text-xs text-slate-500">Envío semi-automático de comprobantes y solvencias</p></div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1">
              <li><b>«WhatsApp semi-auto»</b>: cada pago validado / solvencia / planilla genera un link <code className="text-xs bg-slate-100 px-1 rounded">wa.me</code> con mensaje pre-cargado. Un clic del funcionario y ya se envía desde su WhatsApp Web/celular.</li>
              <li><b>«Email automático (Resend)»</b>: si <code className="text-xs bg-slate-100 px-1 rounded">RESEND_API_KEY</code> está configurada en <code>backend/.env</code>, los recibos se envían por email adjunto sin acción manual.</li>
              <li>Formato Bs. es-VE: <code>Bs. 1.234,56</code>. Teléfono normalizado a E164 (Venezuela = 58).</li>
              <li>Idempotente: no se envía dos veces la misma notificación.</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Card className="p-4 border-orange-100 bg-orange-50/40 text-center">
        <p className="text-sm text-orange-900">
          <b>SISAUR</b> · Backoffice de Funcionarios · <span className="text-orange-700/70">Palma Plus, Saneamiento Urbano</span>
        </p>
      </Card>
    </div>
  );
}
